import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  useClearHistoryMutation,
  useGetSearchHistoryMutation,
  useGetSearchResultMutation,
  useRemoveHistoryMutation,
  useGetQueryResultMutation,
} from "../../services/Post";
import { toast } from "react-toastify";
import { getLanguage } from "../../javascript/SelectLanguage";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { setIds } from "../../app/slices/localSlice";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";

export default function Search() {
  const accountType = useSelector((state) => state?.local?.accountType);
  const userToken = useSelector((state) => state?.local?.token);
  const apiKey = process.env.REACT_APP_GOOGLE_VISION_API_KEY;

  const [searchresults, { isLoading }] = useGetSearchResultMutation();
  const [searchHistory, { isLoading: loader }] = useGetSearchHistoryMutation();
  const [removeHistory, { isLoading: loaders }] = useRemoveHistoryMutation();
  const [clearHistory, { isLoading: loaderss }] = useClearHistoryMutation();
  const [getQuery, { isLoading: loadersss }] = useGetQueryResultMutation();

  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();

  const [searchList, setSearchList] = useState([]);
  const [searchHistoryList, setSearchHistoryList] = useState([]);
  const [activeTab, setActiveTab] = useState("product");
  const [showHistory, setShowHistory] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);

  const [mobileSearchList, setMobileSearchList] = useState([]);
  const [mobileSellerSearchList, setMobileSellerSearchList] = useState([]);
  const [mobileShowHistory, setMobileShowHistory] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState("product");
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(false);

  const [loadingIds, setLoadingIds] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [isImageProcessing, setIsImageProcessing] = useState(false);

  const desktopFileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);

  const isSpeechRecognitionSupported = () => {
    return "webkitSpeechRecognition" in window || "SpeechRecognition" in window;
  };

  const speechRecognition = useRef(null);

  useEffect(() => {
    if (isSpeechRecognitionSupported()) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      speechRecognition.current = new SpeechRecognition();
      speechRecognition.current.continuous = false;
      speechRecognition.current.interimResults = false;
      speechRecognition.current.lang = getLanguage();

      speechRecognition.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setValue("search", transcript);
        setValue("mobileSearch", transcript);
        setIsListening(false);
      };

      speechRecognition.current.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        setIsListening(false);
        toast.error(t("Voice input failed. Please try again."));
      };

      speechRecognition.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (speechRecognition.current) {
        speechRecognition.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    setValue("search", "");
    dispatch(setIds(""));

    if (isSpeechRecognitionSupported()) {
      setIsListening(true);
      openModal();
      try {
        speechRecognition.current.start();
      } catch (error) {
        console.error("Speech recognition start error", error);
        setIsListening(false);
        toast.error(t("Voice input not available."));
      }
    } else {
      toast.error(t("Voice search is not supported in your browser"));
    }
  };

  const stopListening = () => {
    if (speechRecognition.current) {
      speechRecognition.current.stop();
      setIsListening(false);
    }
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageSearch = async (event, isMobile = false) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match("image.*")) {
      toast.error(t("Please select an image file"));
      return;
    }

    if (file.size > 4 * 1024 * 1024) {
      toast.error(t("Image size should be less than 4MB"));
      return;
    }

    setIsImageProcessing(true);

    try {
      const base64Image = await getBase64(file);

      const request = {
        requests: [
          {
            image: {
              content: base64Image,
            },
            features: [
              {
                type: "LABEL_DETECTION",
                maxResults: 10,
              },
              {
                type: "TEXT_DETECTION",
                maxResults: 10,
              },
            ],
          },
        ],
      };

      // Call Google Vision API
      const response = await fetch(
        `https://vision.googleapis.com/v1/images:annotate?key=${apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Google Vision API error: ${response.status}`);
      }

      const data = await response.json();

      const labels = data.responses[0].labelAnnotations
        ? data.responses[0].labelAnnotations.map((label) => label.description)
        : [];

      const texts = data.responses[0].textAnnotations
        ? data.responses[0].textAnnotations.map((text) => text.description)
        : [];

      // Combine all descriptions into a single query string
      let searchQuery = "";

      if (texts.length > 0) {
        // fullTextAnnotation has the entire block of detected text
        const fullText = data.responses[0].fullTextAnnotation?.text || "";
        searchQuery = fullText || texts.join(" ");
      }

      if (!searchQuery && labels.length > 0) {
        searchQuery = labels.join(" ");
      }

      if (!searchQuery) {
        toast.error(t("No recognizable content found in the image"));
        setIsImageProcessing(false);
        return;
      }

      // Call your query API with full descriptions
      const queryResponse = await getQuery({
        query: searchQuery,
        userToken,
        language: getLanguage(),
      }).unwrap();

      if (queryResponse.error) {
        toast.error(queryResponse.message || t("Search failed"));
      } else {
        if (isMobile) {
          setMobileShowHistory(false);
          setMobileSearchList(queryResponse.results?.products || []);
          setMobileSellerSearchList(queryResponse.results?.sellers || []);
        } else {
          setShowHistory(false);
          setSearchList(queryResponse.results?.products || []);
          setMobileSellerSearchList(queryResponse.results?.sellers || []);
        }

        toast.success(t("Image search completed"));
      }
    } catch (error) {
      console.error("Image search error:", error);
      toast.error(t("Failed to process image. Please try again."));
    } finally {
      setIsImageProcessing(false);
      if (desktopFileInputRef.current) desktopFileInputRef.current.value = "";
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = "";
    }
  };

  const uniqueHistory = [];

  searchHistoryList?.forEach((item) => {
    const keyword = item.keyword?.toLowerCase();
    if (
      keyword &&
      !uniqueHistory.find((i) => i.keyword?.toLowerCase() === keyword)
    ) {
      uniqueHistory.push(item);
    }
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    control,
    clearErrors,
  } = useForm();
  const watchedValues = watch();

  const handleSearch = async () => {
    if (!watchedValues?.search || watchedValues?.search.trim() === "") {
      setSearchList([]);
      setMobileSellerSearchList([]);
      return;
    }

    const res = await searchresults({
      search: watchedValues?.search,
      language: getLanguage(),
      userToken,
    });
    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      setSearchList(res?.data?.results?.products || []);
      setMobileSellerSearchList(res?.data?.results?.sellers || []);
    }
  };

  const handleMobileSearch = async () => {
    if (
      !watchedValues?.mobileSearch ||
      watchedValues?.mobileSearch.trim() === ""
    ) {
      setMobileSearchList([]);
      setMobileSellerSearchList([]);
      return;
    }

    const res = await searchresults({
      search: watchedValues?.mobileSearch,
      language: getLanguage(),
      userToken,
    });
    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      setMobileSearchList(res?.data?.results?.products || []);
      setMobileSellerSearchList(res?.data?.results?.sellers || []);
    }
  };

  const handleSearchHistory = async () => {
    if (!userToken) return;

    const res = await searchHistory({
      userToken,
    });
    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      setSearchHistoryList(res?.data?.results?.searchHistory || []);
    }
  };

  useEffect(() => {
    if (watchedValues?.search && !showHistory) {
      const debounceTimer = setTimeout(() => {
        handleSearch();
      }, 300);

      return () => clearTimeout(debounceTimer);
    }

    if (watchedValues?.mobileSearch && !mobileShowHistory) {
      const debounceTimer = setTimeout(() => {
        handleMobileSearch();
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [
    watchedValues?.search,
    watchedValues?.mobileSearch,
    showHistory,
    mobileShowHistory,
  ]);

  const handleSearchClick = () => {
    if (!watchedValues?.search || watchedValues?.search.trim() === "") {
      toast.error(t("Search input can not be empty !"));
      return;
    }
    handleSearch();
    setShowHistory(false);
  };

  const handleMobileSearchClick = () => {
    if (
      !watchedValues?.mobileSearch ||
      watchedValues?.mobileSearch.trim() === ""
    ) {
      toast.error(t("Search input can not be empty !"));
      return;
    }
    handleMobileSearch();
    setMobileShowHistory(false);
  };

  const handleClearClick = () => {
    setValue("search", "");
    setSearchList([]);
    setMobileSellerSearchList([]);
    if (userToken) {
      handleSearchHistory();
      setShowHistory(true);
    }
  };

  const handleMobileClearClick = () => {
    setValue("mobileSearch", "");
    setMobileSearchList([]);
    setMobileSellerSearchList([]);
    if (userToken) {
      handleSearchHistory();
      setMobileShowHistory(true);
    }
  };

  const divRef = useRef(null);
  const mobileDivRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (divRef.current && !divRef.current.contains(event.target)) {
        setOpenDropdown(false);
      }
      if (
        mobileDivRef.current &&
        !mobileDivRef.current.contains(event.target)
      ) {
        setMobileOpenDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openModal = () => {
    handleSearchHistory();
    setOpenDropdown(true);
    if (!watchedValues?.search && userToken) {
      handleSearchHistory();
      setShowHistory(true);
    }
  };

  const openMobileModal = () => {
    handleSearchHistory();
    setMobileOpenDropdown(true);
    if (!watchedValues?.mobileSearch && userToken) {
      handleSearchHistory();
      setMobileShowHistory(true);
    }
  };

  const handleSelectThenClose = () => {
    setOpenDropdown(false);
    setMobileOpenDropdown(false);
  };

  const clearAllHistory = async () => {
    const res = await clearHistory({ userToken });
    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      handleSearchHistory();
    }
  };

  const clearSelectedHistory = async (id) => {
    setLoadingIds((prev) => ({ ...prev, [id]: true }));

    const res = await removeHistory({ userToken, id });

    setLoadingIds((prev) => ({ ...prev, [id]: false }));

    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      handleSearchHistory();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowHistory(false);
  };

  const handleMobileTabChange = (tab) => {
    setMobileActiveTab(tab);
    setMobileShowHistory(false);
  };

  const handleHistoryClick = () => {
    setShowHistory(true);
    if (userToken) {
      handleSearchHistory();
    }
  };

  const handleMobileHistoryClick = () => {
    setMobileShowHistory(true);
    if (userToken) {
      handleSearchHistory();
    }
  };

  const handleInputClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openModal();
    dispatch(setIds(""));
  };

  return (
    <>
      <div className="col-lg-4 col-md-2 d-lg-block d-none">
        <div className="form-design">
          <div className="d-flex gap-1">
            <div className="dropdown w-100 search-dropdown">
              <div className="position-relative">
                <input
                  type="text"
                  autoComplete="off"
                  className="form-control search-box"
                  placeholder={t("Search by chemical name, category etc")}
                  {...register("search")}
                  onClick={handleInputClick}
                  style={{
                    paddingRight: "80px",
                  }}
                />
                <div
                  className="position-absolute search-input-icons"
                  style={{
                    right: i18n.language === "he" ? "auto" : "10px",
                    left: i18n.language === "he" ? "10px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    className={`p-0 border-0 microphone ${
                      isListening
                        ? "microphone-on "
                        : "bg-transparent text-dark"
                    }`}
                    onClick={isListening ? stopListening : startListening}
                    title={t("Voice search")}
                  >
                    <i className="fa fa-microphone fs-6"></i>
                  </button>

                  <div className="position-relative">
                    <button
                      type="button"
                      className="btn btn-sm p-0 border-0 text-dark bg-transparent position-relative"
                      title={t("Search by image")}
                      style={{ color: "#6c757d" }}
                      onClick={() => desktopFileInputRef.current?.click()}
                      disabled={isImageProcessing}
                    >
                      {isImageProcessing ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <i className="fa fa-camera fs-6" />
                      )}
                    </button>
                    <input
                      type="file"
                      ref={desktopFileInputRef}
                      className="d-none"
                      accept="image/*"
                      onChange={(e) => handleImageSearch(e, false)}
                    />
                  </div>

                  {watchedValues?.search && (
                    <button
                      className="btn btn-sm p-0 border-0 bg-transparent"
                      onClick={handleClearClick}
                      title={t("Clear search")}
                      style={{ color: "#6c757d" }}
                    >
                      <i className="fa fa-times fs-6" />
                    </button>
                  )}
                </div>
              </div>
              <div
                ref={divRef}
                className={`dropdown-menu w-100 ${openDropdown ? "show" : ""}`}
                style={{
                  display: openDropdown ? "block" : "none",
                }}
              >
                <div className="w-100 px-3">
                  {uniqueHistory?.length > 0 && (
                    <div className="text-end">
                      <button
                        className="bg-transparent border-0 p-0 fw-light"
                        onClick={() => clearAllHistory()}
                      >
                        <span className="text-decoration-underline">
                          {loaderss ? (
                            <Spinner
                              style={{ height: "20px", width: "20px" }}
                            />
                          ) : (
                            t("Clear All")
                          )}
                        </span>
                      </button>
                    </div>
                  )}
                  <ul className="nav nav-tabs" id="myTabs" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${
                          activeTab === "product" && !showHistory
                            ? "active"
                            : ""
                        }`}
                        onClick={() => handleTabChange("product")}
                        id="product-tab"
                        type="button"
                        role="tab"
                      >
                        {t("Products")}
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${
                          activeTab === "seller" && !showHistory ? "active" : ""
                        }`}
                        onClick={() => handleTabChange("seller")}
                        id="seller-tab"
                        type="button"
                        role="tab"
                      >
                        {t("Seller")}
                      </button>
                    </li>
                    {userToken && (
                      <li className="nav-item" role="presentation">
                        <button
                          className={`nav-link ${showHistory ? "active" : ""}`}
                          onClick={handleHistoryClick}
                          id="history-tab"
                          type="button"
                          role="tab"
                        >
                          {t("History")}
                        </button>
                      </li>
                    )}
                  </ul>
                  <div className="tab-content mt-3" id="myTabsContent">
                    {showHistory ? (
                      <div className="tab-pane fade show active">
                        {uniqueHistory?.length > 0 ? (
                          <>
                            <p className="fw-bold">{t("Recent Searches")}</p>
                            {uniqueHistory?.map((history, index) => (
                              <div
                                key={index}
                                className="row align-items-center hover-items py-2 rounded"
                              >
                                <div className="col-md-2">
                                  <div className="border-none rounded p-1">
                                    <i className="fa fa-history text-sm"></i>
                                  </div>
                                </div>
                                <div
                                  className="col-md-8 ps-0"
                                  onClick={() => {
                                    setValue("search", history?.keyword);
                                    setShowHistory(false);
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  <h2 className="fs-14 m-0">
                                    {history?.keyword}
                                  </h2>
                                </div>
                                <div className="col-md-2 text-end">
                                  <button
                                    className="bg-transparent border-0 p-0"
                                    onClick={() => {
                                      clearSelectedHistory(history?._id);
                                    }}
                                  >
                                    {loadingIds[history?._id] ? (
                                      <Spinner
                                        style={{
                                          height: "8px",
                                          width: "8px",
                                          fontSize: "5px",
                                        }}
                                      />
                                    ) : (
                                      <i className="fa fa-times text-sm"></i>
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <p className="text-center text-muted py-3">
                            {t("No search history found")}
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        <div
                          className={`tab-pane fade ${
                            activeTab === "product" ? "show active" : ""
                          }`}
                          id="product"
                          role="tabpanel"
                          aria-labelledby="product-tab"
                        >
                          {isLoading || loadersss ? (
                            <Skeleton height={30} width={380} count={10} />
                          ) : searchList.length > 0 ? (
                            searchList?.map((ite, ind) => {
                              return (
                                <Link
                                  key={ite?._id}
                                  className="w-100"
                                  to={
                                    accountType === "Buyer"
                                      ? `/product-details/${ite?._id}`
                                      : `/seller-product-details/${ite?._id}`
                                  }
                                  onClick={() => {
                                    dispatch(setIds(ite?._id));
                                    handleSelectThenClose();
                                    setValue(
                                      "search",
                                      i18n.language === "ar"
                                        ? ite?.name_ar
                                        : i18n.language === "bn"
                                        ? ite?.name_bn
                                        : ite?.name_en
                                    );
                                  }}
                                >
                                  <div className="row align-items-center hover-items py-2 rounded">
                                    <div className="col-md-2">
                                      <div className="border rounded p-1 search-result-img">
                                        {ite?.profileImage ? (
                                          <img
                                            src={
                                              ite?.profileImage ||
                                              "assets/img/icon/big-user.png"
                                            }
                                            className="w-100 h-100"
                                            alt="profile"
                                          />
                                        ) : (
                                          <i className="fa fa-image"></i>
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-md-8 ps-0">
                                      <h2 className="fs-14 m-0">
                                        {i18n.language === "ar"
                                          ? ite?.name_ar
                                          : i18n.language === "bn"
                                          ? ite?.name_bn
                                          : ite?.name_en}
                                      </h2>
                                      <p className="m-0 fs-10 text-light">
                                        {(() => {
                                          const description =
                                            i18n.language === "ar"
                                              ? ite?.description_ar
                                              : i18n.language === "bn"
                                              ? ite?.description_bn
                                              : ite?.description_en;
                                          return description?.length > 100
                                            ? `${description.slice(0, 100)}...`
                                            : description || t("N/A");
                                        })()}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              );
                            })
                          ) : (
                            watchedValues?.search && (
                              <p className="text-center text-muted py-3">
                                {t("No results found")}
                              </p>
                            )
                          )}
                        </div>
                        <div
                          className={`tab-pane fade ${
                            activeTab === "seller" ? "show active" : ""
                          }`}
                          id="seller"
                          role="tabpanel"
                          aria-labelledby="seller-tab"
                        >
                          {isLoading || loadersss ? (
                            <Skeleton height={30} width={380} count={10} />
                          ) : mobileSellerSearchList.length > 0 ? (
                            mobileSellerSearchList?.map((ite, ind) => {
                              return (
                                <Link
                                  key={ite?._id}
                                  to="/seller-details"
                                  state={{
                                    sellerId: ite?._id,
                                  }}
                                  className="w-100"
                                  onClick={() => {
                                    dispatch(setIds(ite?._id));
                                    handleSelectThenClose();
                                  }}
                                >
                                  <div className="row align-items-center hover-items py-2 rounded">
                                    <div className="col-md-2">
                                      <div className="border rounded search-result-img p-1">
                                        {ite?.profileImage ? (
                                          <img
                                            src={
                                              ite?.profileImage ||
                                              "assets/img/icon/big-user.png"
                                            }
                                            className="w-100 h-100"
                                            alt="profile"
                                          />
                                        ) : (
                                          <i className="fa fa-image"></i>
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-md-8 ps-0">
                                      <h2 className="fs-14 m-0">
                                        {ite?.fullName}
                                      </h2>
                                      <p className="m-0 fs-10 text-light">
                                        {ite?.businessName || "N/A"}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              );
                            })
                          ) : (
                            watchedValues?.search && (
                              <p className="text-center text-muted py-3">
                                {t("No results found")}
                              </p>
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <button
              className="search-icon bg-main rounded-circle cursor-pointer d-none"
              onClick={handleSearchClick}
            >
              <img src="/assets/img/icon/search-normal.png" alt="search" />
            </button>
          </div>
        </div>
      </div>

      <div className="col-md-2 col-2 d-lg-none d-block text-end order-md-2 order-1 pe-0">
        <div className="search-dropdown-wrapper-tab">
          <div className="dropdown w-100 search-dropdown">
            <button
              className="search-icon bg-main rounded-circle cursor-pointer"
              onClick={openMobileModal}
            >
              <img src="/assets/img/icon/search-normal.png" alt="search" />
            </button>
            <div
              ref={mobileDivRef}
              className={`dropdown-menu w-100 ${
                mobileOpenDropdown ? "show" : ""
              }`}
              style={{
                display: mobileOpenDropdown ? "block" : "none",
              }}
            >
              <div className="w-100 px-3">
                {uniqueHistory?.length > 0 && (
                  <div className="text-end">
                    <button
                      className="bg-transparent border-0 p-0 fw-light"
                      onClick={() => clearAllHistory()}
                    >
                      <span className="text-decoration-underline">
                        {loaderss ? (
                          <Spinner style={{ height: "20px", width: "20px" }} />
                        ) : (
                          t("Clear All")
                        )}
                      </span>
                    </button>
                  </div>
                )}
                <div className="d-flex gap-1 py-3 position-relative">
                  <input
                    type="text"
                    autoComplete="off"
                    className="form-control search-box"
                    placeholder={t("Search by chemical name, category etc")}
                    {...register("mobileSearch")}
                    style={{
                      paddingRight: "70px",
                    }}
                  />
                  <div
                    className="position-absolute search-input-icons"
                    style={{
                      right: i18n.language === "he" ? "auto" : "10px",
                      left: i18n.language === "he" ? "10px" : "auto",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <button
                      type="button"
                      className={`p-0 border-0 microphone ${
                        isListening
                          ? "microphone-on "
                          : "bg-transparent text-dark"
                      }`}
                      onClick={isListening ? stopListening : startListening}
                      title={t("Voice search")}
                    >
                      <i className="fa fa-microphone fs-6"></i>
                    </button>
                    <div className="position-relative">
                      <button
                        type="button"
                        className="p-0 border-0 bg-transparent position-relative"
                        title={t("Search by image")}
                        style={{ color: "#6c757d" }}
                        onClick={() => mobileFileInputRef.current?.click()}
                        disabled={isImageProcessing}
                      >
                        {isImageProcessing ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="fa fa-camera fs-6" />
                        )}
                      </button>
                      <input
                        type="file"
                        ref={mobileFileInputRef}
                        className="d-none"
                        accept="image/*"
                        onChange={(e) => handleImageSearch(e, true)}
                      />
                    </div>

                    {watchedValues?.mobileSearch && (
                      <button
                        className="btn btn-sm p-0 border-0 bg-transparent"
                        onClick={handleMobileClearClick}
                        title={t("Clear search")}
                        style={{ color: "#6c757d" }}
                      >
                        <i className="fa fa-times fs-6" />
                      </button>
                    )}
                  </div>
                  <button
                    className="search-icon bg-main rounded-circle cursor-pointer d-none"
                    onClick={handleMobileSearchClick}
                  >
                    <img
                      src="/assets/img/icon/search-normal.png"
                      alt="search"
                    />
                  </button>
                </div>

                <ul className="nav nav-tabs" id="mobileTabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${
                        mobileActiveTab === "product" && !mobileShowHistory
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleMobileTabChange("product")}
                    >
                      {t("Products")}
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${
                        mobileActiveTab === "seller" && !mobileShowHistory
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleMobileTabChange("seller")}
                    >
                      {t("Seller")}
                    </button>
                  </li>
                  {userToken && (
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${
                          mobileShowHistory ? "active" : ""
                        }`}
                        onClick={handleMobileHistoryClick}
                      >
                        {t("History")}
                      </button>
                    </li>
                  )}
                </ul>

                <div className="tab-content mt-3">
                  {mobileShowHistory ? (
                    <div className="tab-pane fade show active">
                      {uniqueHistory?.length > 0 ? (
                        <>
                          <p className="fw-bold">{t("Recent Searches")}</p>
                          {uniqueHistory?.map((history, index) => (
                            <div
                              key={index}
                              className="row align-items-center hover-items py-2 rounded"
                            >
                              <div className="col-2">
                                <div className="border-none rounded p-1">
                                  <i className="fa fa-history text-sm"></i>
                                </div>
                              </div>
                              <div
                                className="col-8 ps-0"
                                onClick={() => {
                                  setValue("mobileSearch", history?.keyword);
                                  setMobileShowHistory(false);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <h2 className="fs-14 m-0">
                                  {history?.keyword}
                                </h2>
                              </div>
                              <div className="col-2 text-end">
                                <button
                                  className="bg-transparent border-0 p-0"
                                  onClick={() => {
                                    clearSelectedHistory(history?._id);
                                  }}
                                >
                                  {loadingIds[history?._id] ? (
                                    <Spinner
                                      style={{
                                        height: "8px",
                                        width: "8px",
                                        fontSize: "5px",
                                      }}
                                    />
                                  ) : (
                                    <i className="fa fa-times text-sm"></i>
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <p className="text-center text-muted py-3">
                          {t("No search history found")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div
                        className={`tab-pane fade ${
                          mobileActiveTab === "product" ? "show active" : ""
                        }`}
                      >
                        {isLoading || loadersss ? (
                          <Skeleton height={30} width={"100%"} count={3} />
                        ) : mobileSearchList?.length > 0 ? (
                          mobileSearchList?.map((ite, ind) => {
                            return (
                              <Link
                                key={ite?._id}
                                className="w-100"
                                to={
                                  accountType === "Buyer"
                                    ? `/product-details/${ite?._id}`
                                    : `/seller-product-details/${ite?._id}`
                                }
                                onClick={() => {
                                  dispatch(setIds(ite?._id));
                                  handleSelectThenClose();
                                  setValue(
                                    "mobileSearch",
                                    i18n.language === "ar"
                                      ? ite?.name_ar
                                      : i18n.language === "bn"
                                      ? ite?.name_bn
                                      : ite?.name_en
                                  );
                                }}
                              >
                                <div className="row align-items-center hover-items py-2 rounded">
                                  <div className="col-3">
                                    <div className="border rounded p-1 search-result-img">
                                      {ite?.profileImage ? (
                                        <img
                                          src={
                                            ite?.profileImage ||
                                            "assets/img/icon/big-user.png"
                                          }
                                          className="w-100 h-100"
                                          alt="profile"
                                        />
                                      ) : (
                                        <i className="fa fa-image"></i>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-9 ps-0">
                                    <h2 className="fs-14 m-0">
                                      {i18n.language === "ar"
                                        ? ite?.name_ar
                                        : i18n.language === "bn"
                                        ? ite?.name_bn
                                        : ite?.name_en}
                                    </h2>
                                    <p className="m-0 fs-10 text-light">
                                      {(() => {
                                        const description =
                                          i18n.language === "ar"
                                            ? ite?.description_ar
                                            : i18n.language === "bn"
                                            ? ite?.description_bn
                                            : ite?.description_en;
                                        return description?.length > 50
                                          ? `${description.slice(0, 50)}...`
                                          : description || t("N/A");
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            );
                          })
                        ) : (
                          watchedValues?.mobileSearch && (
                            <p className="text-center text-muted py-3">
                              {t("No results found")}
                            </p>
                          )
                        )}
                      </div>
                      <div
                        className={`tab-pane fade ${
                          mobileActiveTab === "seller" ? "show active" : ""
                        }`}
                      >
                        {isLoading || loadersss ? (
                          <Skeleton height={30} width={"100%"} count={3} />
                        ) : mobileSellerSearchList.length > 0 ? (
                          mobileSellerSearchList?.map((ite, ind) => {
                            return (
                              <Link
                                key={ite?._id}
                                to="/seller-details"
                                state={{
                                  sellerId: ite?._id,
                                }}
                                className="w-100"
                                onClick={() => {
                                  dispatch(setIds(ite?._id));
                                  handleSelectThenClose();
                                }}
                              >
                                <div className="row align-items-center hover-items py-2 rounded">
                                  <div className="col-3">
                                    <div className="border rounded search-result-img p-1">
                                      {ite?.profileImage ? (
                                        <img
                                          src={
                                            ite?.profileImage ||
                                            "assets/img/icon/big-user.png"
                                          }
                                          className="w-100 h-100"
                                          alt="profile"
                                        />
                                      ) : (
                                        <i className="fa fa-image"></i>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-9 ps-0">
                                    <h2 className="fs-14 m-0">
                                      {ite?.fullName}
                                    </h2>
                                    <p className="m-0 fs-10 text-light">
                                      {ite?.businessName || "N/A"}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            );
                          })
                        ) : (
                          watchedValues?.mobileSearch && (
                            <p className="text-center text-muted py-3">
                              {t("No results found")}
                            </p>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
